import sharedTransformForSubmit from '../../shared/config/submit-transformer';

const CLAIMANT_RELATIONSHIP_MAP = {
  currentSpouseOption: 'spouse',
  child18To23InSchoolOption: 'child_18_23',
  custodianForChildUnder18Option: 'custodian',
  dependentParentOption: 'parent',
  childOver18IncapableOption: 'child_disabled',
  otherOption: 'other',
};

const APPORTIONMENT_PERSON_RELATIONSHIP_MAP = {
  currentSpouse: 'spouse',
  child18To23InSchool: 'child 18-23',
  custodianForChildUnder18: 'child under 18',
  childOver18Incapable: 'child unable to self-support',
  dependentParent: 'dependent parent',
  other: 'other',
};

const CHILD_APPORTIONMENT_RELATIONSHIP_VALUES = [
  'child',
  'child18To23InSchool',
  'custodianForChildUnder18',
  'childOver18Incapable',
  'other',
  'child 18-23',
  'child under 18',
  'child unable to self-support',
];

const REASON_ORDER = [
  {
    answer: 'veteranIncarceratedOver60Days',
    reason: 'veteran_incarcerated',
    facilityName: 'veteranIncarcerationFacilityName',
    facilityAddress: 'veteranIncarcerationFacilityAddress',
    convictionType: 'veteranIncarcerationFelonyOrMisdemeanor',
  },
  {
    answer: 'survivingBeneficiaryIncarceratedOver60Days',
    reason: 'spouse_or_child_incarcerated',
    facilityName: 'survivingBeneficiaryFacilityName',
    facilityAddress: 'survivingBeneficiaryFacilityAddress',
    convictionType: 'survivingBeneficiaryIncarcerationFelonyOrMisdemeanor',
  },
  {
    answer: 'veteranIncompetentInCare',
    reason: 'veteran_incompetent_no_fiduciary',
    facilityName: 'veteranCompetenceFacilityName',
    facilityAddress: 'veteranCompetenceFacilityAddress',
  },
  {
    answer: 'veteranReceivingPensionInCare',
    reason: 'veteran_pension_care_facility',
    facilityName: 'veteranPensionFacilityName',
    facilityAddress: 'veteranPensionFacilityAddress',
  },
  {
    answer: 'beneficiaryInEnemyTerritory',
    reason: 'enemy_territory_resident',
  },
  {
    answer: 'veteranDisappeared',
    reason: 'veteran_disappeared',
  },
];

const isYesValue = value => value === true || value === 'Y' || value === 'yes';

const isNoValue = value => value === false || value === 'N' || value === 'no';

const answerToBoolean = value => {
  if (isYesValue(value)) {
    return true;
  }

  if (isNoValue(value)) {
    return false;
  }

  return undefined;
};

const pruneEmpty = value => {
  if (Array.isArray(value)) {
    const prunedArray = value
      .map(item => pruneEmpty(item))
      .filter(item => item !== undefined);

    return prunedArray.length ? prunedArray : undefined;
  }

  if (value && typeof value === 'object') {
    const pruned = Object.entries(value).reduce((acc, [key, val]) => {
      const cleaned = pruneEmpty(val);

      if (cleaned !== undefined) {
        acc[key] = cleaned;
      }

      return acc;
    }, {});

    return Object.keys(pruned).length ? pruned : undefined;
  }

  if (value === '' || value === null || value === undefined) {
    return undefined;
  }

  return value;
};

const joinName = (name = {}) =>
  [name.first, name.middle, name.last].filter(Boolean).join(' ').trim();

const digitsOnly = (s = '') => String(s).replace(/\D/g, '');

const formatSsn = (s = '') => {
  const d = digitsOnly(s);
  return d.length === 9 ? `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}` : s;
};

const isChildApportionmentRelationship = relationship =>
  CHILD_APPORTIONMENT_RELATIONSHIP_VALUES.includes(relationship);

const buildAddress = (address = {}) => ({
  street: address.street,
  street2: address.street2,
  city: address.city,
  state: address.state,
  postalCode: address.postalCode,
  country: address.country,
});

const buildApportionmentPeople = (people = []) =>
  people.map(person => {
    const needsChildFields = isChildApportionmentRelationship(
      person.veteranRelationshipDescription,
    );

    return {
      fullName: joinName(person.fullName),
      ssn: formatSsn(person.ssn),
      relationship:
        APPORTIONMENT_PERSON_RELATIONSHIP_MAP[
          person.veteranRelationshipDescription
        ] || person.veteranRelationshipDescription,
      otherRelationshipDescription: person.otherRelationshipDescription,
      currentlyReceiving: answerToBoolean(person.receivesApportionmentNow),
      isStepchild: needsChildFields
        ? answerToBoolean(person.isStepChild)
        : undefined,
      stepchildLivesWithVeteran: needsChildFields
        ? answerToBoolean(person.stepchildLivesWithVeteran)
        : undefined,
      stepchildDepartureDate: needsChildFields
        ? person.stepchildDepartureDate
        : undefined,
      legallyAdopted: needsChildFields
        ? answerToBoolean(person.adoptedChildrenQuestion)
        : undefined,
    };
  });

const findActiveReason = (apportionment = {}) =>
  REASON_ORDER.find(reason => isYesValue(apportionment[reason.answer]));

const buildReasonFields = (apportionment = {}) => {
  const activeReason = findActiveReason(apportionment);

  if (!activeReason) {
    return {};
  }

  const reasonFields = {
    reason: activeReason.reason,
  };

  if (activeReason.convictionType) {
    const convictionType = apportionment[activeReason.convictionType];

    reasonFields.incarceration = {
      felony: convictionType === 'felony',
      misdemeanor: convictionType === 'misdemeanor',
    };
  }

  if (activeReason.facilityName) {
    reasonFields.facilityName = apportionment[activeReason.facilityName];
  }

  if (activeReason.facilityAddress) {
    reasonFields.facilityAddress = apportionment[activeReason.facilityAddress];
  }

  return reasonFields;
};

const anyStepchildLivingInHousehold = (people = []) => {
  const stepchildLivingAnswers = people
    .filter(person =>
      isChildApportionmentRelationship(person.veteranRelationshipDescription),
    )
    .filter(person => isYesValue(person.isStepChild))
    .map(person => answerToBoolean(person.stepchildLivesWithVeteran))
    .filter(answer => answer !== undefined);

  if (!stepchildLivingAnswers.length) {
    return undefined;
  }

  return stepchildLivingAnswers.some(Boolean);
};

const anyChildLegallyAdopted = (people = []) => {
  const adoptedAnswers = people
    .filter(person =>
      isChildApportionmentRelationship(person.veteranRelationshipDescription),
    )
    .map(person => answerToBoolean(person.adoptedChildrenQuestion))
    .filter(answer => answer !== undefined);

  if (!adoptedAnswers.length) {
    return undefined;
  }

  return adoptedAnswers.some(Boolean);
};

export const buildPayload = (formConfig, data = {}) => {
  const veteran = data.veteran || {};
  const claimant = data.claimant || {};
  const people = data.requestedApportionmentPeople || [];
  const apportionment = data.apportionment || {};

  return pruneEmpty({
    formNumber: formConfig?.formId || '21-0788',
    fullName: veteran.fullName,
    dateOfBirth: veteran.dateOfBirth,
    ssn: formatSsn(veteran.ssn),
    phone: digitsOnly(claimant.phone?.contact),
    vaFileNumber: veteran.vaFileNumber,
    email: claimant.email,
    emailAddress: claimant.email,
    onBehalfOfChild:
      claimant.relationshipType === 'custodianForChildUnder18'
        ? joinName(claimant.fullName)
        : undefined,
    relationshipToVeteran:
      CLAIMANT_RELATIONSHIP_MAP[claimant.relationshipType] ||
      claimant.relationshipType,
    otherRelationshipDescription:
      claimant.relationshipType === 'otherOption'
        ? claimant.otherRelationshipDescription
        : undefined,
    preparerIsVeteran: false,
    preparer: claimant.fullName,
    statementOfTruthSignature: data.statementOfTruthSignature,
    statementOfTruthDate: data.statementOfTruthSignatureDate,
    address: buildAddress(claimant.address),
    apportionmentPeople: buildApportionmentPeople(people),
    legallyAdopted: anyChildLegallyAdopted(people),
    stepchildLivingInHousehold: anyStepchildLivingInHousehold(people),
    ...buildReasonFields(apportionment),
    signatureDate: data.statementOfTruthSignatureDate,
    remarks: data.remarks,
  });
};

export default function transformForSubmit(formConfig, form) {
  const transformed = JSON.parse(
    sharedTransformForSubmit(formConfig, form, {
      allowPartialAddress: true,
    }),
  );

  const payload = buildPayload(formConfig, transformed);
  return JSON.stringify(payload);
}
